import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import { BlogCard } from './BlogIndex';
import gaHero from '../../assets/images/heroes/ga-hero-main.webp';
import ncHero from '../../assets/images/heroes/nc-hero-main.webp';

const BRAND_HEROES = { gso: gaHero, 'apex-nc': ncHero, 'apex-tn': ncHero };
const PAGE_SIZE = 12;
const CATEGORIES = ['treatment', 'conditions', 'prevention', 'news', 'legal', 'general'];

function filterArticlesByBrand(articles, brandKey) {
  if (brandKey === 'gso') return articles.filter(a => a.source && a.source.includes('gaspineortho'));
  if (brandKey === 'apex-nc') return articles.filter(a => a.source && a.source.includes('apexosn'));
  return articles;
}

export default function BlogCategory() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandKey = brand.brandKey || 'gso';
  const heroImg = BRAND_HEROES[brandKey] || gaHero;
  const baseDomain = brand?.domain || 'gaspineortho.com';

  const [page, setPage] = useState(1);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reset page when category changes
  useEffect(() => { setPage(1); }, [categorySlug]);

  useEffect(() => {
    fetch('/data/articles-scraped.json')
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load articles: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setAllArticles(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load articles:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const brandFiltered = filterArticlesByBrand(allArticles, brandKey);
  const categoryFiltered = brandFiltered.filter(a => (a.category || 'general') === categorySlug);
  const totalPages = Math.ceil(categoryFiltered.length / PAGE_SIZE);
  const visible = categoryFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const categoryLabel = t(`blog.category.${categorySlug}`, locale);

  // Category counts for filter bar
  const categoryCounts = {};
  for (const a of brandFiltered) {
    const cat = a.category || 'general';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  }

  return (
    <div className="blog-index">
      <Helmet>
        <title>{categoryLabel} | {brand.name} Blog</title>
        <meta name="description" content={t('blog.category.metaDescription', locale).replace('{{category}}', categoryLabel).replace('{{name}}', brand.name)} />
        <link rel="canonical" href={`https://${baseDomain}/blog/category/${categorySlug}`} />
        <meta property="og:title" content={`${categoryLabel} | ${brand.name} Blog`} />
        <meta property="og:description" content={t('blog.category.metaDescription', locale).replace('{{category}}', categoryLabel).replace('{{name}}', brand.name)} />
        <meta property="og:url" content={`https://${baseDomain}/blog/category/${categorySlug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brand.brandKey}/logo-color.webp`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/blog/category/${categorySlug}`} />

      <section
        className="blog-hero"
        style={{
          backgroundImage: `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${heroImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container">
          <p style={{ opacity: 0.75, fontSize: '0.9rem', marginBottom: 8 }}>
            <Link to={localizedPath('/blog', locale)} style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'underline' }}>
              {t('blog.title', locale)}
            </Link>
            {' › '}
            {categoryLabel}
          </p>
          <h1>{categoryLabel}</h1>
          {!loading && <p style={{ opacity: 0.75, fontSize: '0.95rem' }}>{categoryFiltered.length} articles</p>}
        </div>
      </section>

      <section className="blog-grid-section section-padding">
        <div className="container">
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-text-muted)' }}>
              <p>{t("common.loading", locale)}</p>
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-error, #e53e3e)' }}>
              <p>{t('blog.loadError', locale)}</p>
            </div>
          )}
          {!loading && !error && (
            <>
              {/* Category filter bar */}
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  overflowX: 'auto',
                  paddingBottom: 8,
                  marginBottom: 28,
                  flexWrap: 'wrap',
                }}
              >
                <button
                  className="glass-badge"
                  onClick={() => navigate(localizedPath('/blog', locale))}
                  style={{
                    cursor: 'pointer',
                    border: '1px solid var(--color-border)',
                    background: 'transparent',
                    color: 'var(--color-text)',
                    padding: '6px 14px',
                    borderRadius: 20,
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    textDecoration: 'none',
                  }}
                >
                  {t('blog.category.all', locale)} ({brandFiltered.length})
                </button>
                {CATEGORIES.filter(cat => categoryCounts[cat] > 0).map(cat => (
                  <button
                    key={cat}
                    className="glass-badge"
                    onClick={() => navigate(localizedPath(`/blog/category/${cat}`, locale))}
                    style={{
                      cursor: 'pointer',
                      border: cat === categorySlug ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                      background: cat === categorySlug ? 'var(--color-primary)' : 'transparent',
                      color: cat === categorySlug ? '#fff' : 'var(--color-text)',
                      fontWeight: cat === categorySlug ? 700 : 400,
                      padding: '6px 14px',
                      borderRadius: 20,
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap',
                      textDecoration: 'none',
                    }}
                  >
                    {t(`blog.category.${cat}`, locale)} ({categoryCounts[cat] || 0})
                  </button>
                ))}
              </div>

              {visible.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-text-muted)' }}>
                  <p>{t('blog.noArticles', locale)}</p>
                  <Link to={localizedPath('/blog', locale)} style={{ color: 'var(--color-accent)', fontWeight: 600 }}>
                    {t('blog.backToBlog', locale)}
                  </Link>
                </div>
              ) : (
                <div className="blog-card-grid">
                  {visible.map((article) => (
                    <BlogCard key={article.slug} article={article} locale={locale} />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 40, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                    disabled={page === 1}
                    className="glass-btn glass-btn-outline"
                    style={{ padding: '8px 16px', fontSize: '0.9rem' }}
                  >
                    {t('blog.prev', locale)}
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      onClick={() => { setPage(n); window.scrollTo(0, 0); }}
                      className={`glass-btn ${n === page ? 'glass-btn-primary' : 'glass-btn-outline'}`}
                      style={{ padding: '8px 14px', fontSize: '0.9rem', minWidth: 40 }}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                    disabled={page === totalPages}
                    className="glass-btn glass-btn-outline"
                    style={{ padding: '8px 16px', fontSize: '0.9rem' }}
                  >
                    {t('blog.next', locale)}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
